/**
 * Reddit Pixel helpers.
 *
 * SignUp and Purchase are fired from the same success-path gates as Meta:
 * brand-new account registration and personal premium activation.
 * Phone is intentionally omitted until collected.
 *
 * Pass raw identity values — Reddit hashes match keys before storing them.
 */

export const REDDIT_PIXEL_ID = "a2_ilj2r0mc2w1g";

export function getRedditPixelId() {
  if (typeof window !== "undefined") {
    return window.LESSON_LAUNCHPAD_REDDIT_PIXEL_ID || REDDIT_PIXEL_ID;
  }

  return REDDIT_PIXEL_ID;
}

export function hasConfiguredRedditPixelId(pixelId = REDDIT_PIXEL_ID) {
  const normalizedPixelId = String(pixelId || "").trim();
  return Boolean(normalizedPixelId && !normalizedPixelId.includes("REPLACE_WITH"));
}

function normalizeRedditIdentity({ email, externalId } = {}) {
  const payload = {};
  const normalizedEmail = String(email || "").trim().toLowerCase();
  const normalizedExternalId = String(externalId || "").trim();

  if (normalizedEmail) {
    payload.email = normalizedEmail;
  }
  if (normalizedExternalId) {
    payload.externalId = normalizedExternalId;
  }

  return payload;
}

export function identifyRedditUser({ email, externalId } = {}) {
  if (typeof window === "undefined") {
    return { identified: false, reason: "no_window" };
  }

  const payload = normalizeRedditIdentity({ email, externalId });
  if (!Object.keys(payload).length) {
    return { identified: false, reason: "missing_identity" };
  }

  const pixelId = getRedditPixelId();

  if (!hasConfiguredRedditPixelId(pixelId)) {
    return { identified: false, reason: "missing_pixel_id", payload };
  }

  if (typeof window.rdt !== "function") {
    return { identified: false, reason: "rdt_unavailable", payload };
  }

  window.rdt("init", pixelId, payload);
  return { identified: true, payload };
}

export function trackRedditSignUp({ conversionId, email, externalId } = {}) {
  if (typeof window === "undefined") {
    return { tracked: false, reason: "no_window" };
  }

  const normalizedConversionId = String(conversionId || "").trim();
  const identity = normalizeRedditIdentity({ email, externalId });
  const properties = {
    ...(normalizedConversionId ? { conversionId: normalizedConversionId } : {}),
    ...identity,
  };

  const pixelId = getRedditPixelId();

  if (!hasConfiguredRedditPixelId(pixelId)) {
    return { tracked: false, reason: "missing_pixel_id", conversionId: normalizedConversionId || undefined };
  }

  if (typeof window.rdt !== "function") {
    return { tracked: false, reason: "rdt_unavailable", conversionId: normalizedConversionId || undefined };
  }

  window.rdt("track", "SignUp", properties);
  return { tracked: true, conversionId: normalizedConversionId || undefined };
}

export function trackRedditPurchase({
  conversionId,
  value,
  currency = "USD",
  email,
  externalId,
} = {}) {
  if (typeof window === "undefined") {
    return { tracked: false, reason: "no_window" };
  }

  const normalizedConversionId = String(conversionId || "").trim();
  const normalizedCurrency = String(currency || "USD").trim().toUpperCase();
  const parsedValue = Number(value);
  const resolvedValue = Number.isFinite(parsedValue) && parsedValue > 0 ? parsedValue : undefined;
  const identity = normalizeRedditIdentity({ email, externalId });
  const properties = {
    ...(normalizedConversionId ? { conversionId: normalizedConversionId } : {}),
    ...(resolvedValue !== undefined ? { value: resolvedValue } : {}),
    currency: normalizedCurrency,
    ...identity,
  };

  const pixelId = getRedditPixelId();

  if (!hasConfiguredRedditPixelId(pixelId)) {
    return { tracked: false, reason: "missing_pixel_id", conversionId: normalizedConversionId || undefined };
  }

  if (typeof window.rdt !== "function") {
    return { tracked: false, reason: "rdt_unavailable", conversionId: normalizedConversionId || undefined };
  }

  window.rdt("track", "Purchase", properties);
  return { tracked: true, conversionId: normalizedConversionId || undefined };
}
